/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Spacer, Spinner, Text } from "@nordcloud/gnui";
import styles from "~/assets/styles/Runbook.module.css";
import { SimpleTable } from "~/components/Runbook/SimpleTable";
import { Divider } from "~/components/Styled/Divider";
import { PagerDutyLink } from "~/components/Utils/PagerDutyLink";
import { usePDEscalationPolicies } from "~/hooks/usePDEscalationPolicies";
import { usePDServices } from "~/hooks/usePDServices";
import { Application } from "~/models";
import { getServicesTableData } from "~/tools/utils/pagerDuty";

type Props = {
  application: Application;
};

function PagerDutyServices({ application }: Props) {
  const { services, servicesLoading } = usePDServices(application.pdCustomer);
  const {
    escalationPolicies,
    escalationPoliciesLoading,
  } = usePDEscalationPolicies();

  const columns = [
    "Service Name",
    "Integration",
    "Current Escalation Policy",
    "Target Escalation Policy",
  ];

  let servicesNode: React.ReactNode;
  if (!application.pdCustomer || !application.pdServices.length) {
    servicesNode = <Text>No PagerDuty services</Text>;
  } else if (servicesLoading || escalationPoliciesLoading) {
    servicesNode = <Spinner />;
  } else {
    const servicesList = getServicesTableData(
      application.pdServices,
      services,
      escalationPolicies
    );

    servicesNode = (
      <SimpleTable columns={columns}>
        {servicesList.map((s) => (
          <tr key={s.name}>
            <td className={styles.tableElement}>
              <PagerDutyLink type="service" id={s.pagerdutyId}>
                {s.name}
              </PagerDutyLink>
            </td>

            <td className={styles.tableElement}>{s.integration}</td>

            <td className={styles.tableElement}>
              <PagerDutyLink type="escalationPolicy" id={s.epId}>
                {s.epName}
              </PagerDutyLink>
            </td>

            <td className={styles.tableElement}>
              {s.targetEpId ? (
                <PagerDutyLink type="escalationPolicy" id={s.targetEpId}>
                  {s.targetEpName}
                </PagerDutyLink>
              ) : (
                "-"
              )}
            </td>
          </tr>
        ))}
      </SimpleTable>
    );
  }

  return (
    <Divider>
      <Spacer height="1rem" />
      <Text isTitle weight="medium">
        PageDuty Services
      </Text>

      {servicesNode}
      <Spacer height="1rem" />
    </Divider>
  );
}

export { PagerDutyServices };
